import React, { useEffect, useState } from 'react';
import {
     Accordion,
     AccordionDetails,
     AccordionSummary,
     Alert,
     Autocomplete,
     Box,
     Button,
     Dialog,
     DialogActions,
     DialogContent,
     DialogTitle,
     IconButton,
     Link,
     Stack,
     Step,
     StepLabel,
     Stepper,
     TextField,
     Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import amexLogo from '../../assets/amex.png';
import masterCardLogo from '../../assets/mc_logo.svg';
import napsLogo from '../../assets/naps.svg';
import napsWordmark from '../../assets/naps_logo.png';
import visaLogo from '../../assets/visa_logo.svg';
import googlePayLogo from '../../assets/google_pay.png';
import applePayLogo from '../../assets/apple_pay.svg';
import { formatCurrency } from './eventDetailsUtils';
import { registrationDialogStyles } from './eventDetailsStyles';
import { COUNTRY_MAX_LENGTH, EMAIL_MAX_LENGTH, getAdditionalFieldHelperText, getFieldLengthValue, NAME_MAX_LENGTH, PHONE_MAX_LENGTH } from './registrationValidation';

const countries = [
     'Afghanistan',
     'Albania',
     'Algeria',
     'Andorra',
     'Angola',
     'Antigua and Barbuda',
     'Argentina',
     'Armenia',
     'Australia',
     'Austria',
     'Azerbaijan',
     'Bahamas',
     'Bahrain',
     'Bangladesh',
     'Barbados',
     'Belarus',
     'Belgium',
     'Belize',
     'Benin',
     'Bhutan',
     'Bolivia',
     'Bosnia and Herzegovina',
     'Botswana',
     'Brazil',
     'Brunei',
     'Bulgaria',
     'Burkina Faso',
     'Burundi',
     'Cabo Verde',
     'Cambodia',
     'Cameroon',
     'Canada',
     'Central African Republic',
     'Chad',
     'Chile',
     'China',
     'Colombia',
     'Comoros',
     'Congo',
     'Costa Rica',
     'Croatia',
     'Cuba',
     'Cyprus',
     'Czech Republic',
     'Denmark',
     'Djibouti',
     'Dominica',
     'Dominican Republic',
     'Ecuador',
     'Egypt',
     'El Salvador',
     'Equatorial Guinea',
     'Eritrea',
     'Estonia',
     'Eswatini',
     'Ethiopia',
     'Fiji',
     'Finland',
     'France',
     'Gabon',
     'Gambia',
     'Georgia',
     'Germany',
     'Ghana',
     'Greece',
     'Grenada',
     'Guatemala',
     'Guinea',
     'Guinea-Bissau',
     'Guyana',
     'Haiti',
     'Honduras',
     'Hungary',
     'Iceland',
     'India',
     'Indonesia',
     'Iran',
     'Iraq',
     'Ireland',
     'Israel',
     'Italy',
     'Jamaica',
     'Japan',
     'Jordan',
     'Kazakhstan',
     'Kenya',
     'Kiribati',
     'Kuwait',
     'Kyrgyzstan',
     'Laos',
     'Latvia',
     'Lebanon',
     'Lesotho',
     'Liberia',
     'Libya',
     'Liechtenstein',
     'Lithuania',
     'Luxembourg',
     'Madagascar',
     'Malawi',
     'Malaysia',
     'Maldives',
     'Mali',
     'Malta',
     'Marshall Islands',
     'Mauritania',
     'Mauritius',
     'Mexico',
     'Micronesia',
     'Moldova',
     'Monaco',
     'Mongolia',
     'Montenegro',
     'Morocco',
     'Mozambique',
     'Myanmar',
     'Namibia',
     'Nauru',
     'Nepal',
     'Netherlands',
     'New Zealand',
     'Nicaragua',
     'Niger',
     'Nigeria',
     'North Korea',
     'North Macedonia',
     'Norway',
     'Oman',
     'Pakistan',
     'Palau',
     'Palestine',
     'Panama',
     'Papua New Guinea',
     'Paraguay',
     'Peru',
     'Philippines',
     'Poland',
     'Portugal',
     'Qatar',
     'Romania',
     'Russia',
     'Rwanda',
     'Saint Kitts and Nevis',
     'Saint Lucia',
     'Saint Vincent and the Grenadines',
     'Samoa',
     'San Marino',
     'Sao Tome and Principe',
     'Saudi Arabia',
     'Senegal',
     'Serbia',
     'Seychelles',
     'Sierra Leone',
     'Singapore',
     'Slovakia',
     'Slovenia',
     'Solomon Islands',
     'Somalia',
     'South Africa',
     'South Korea',
     'South Sudan',
     'Spain',
     'Sri Lanka',
     'Sudan',
     'Suriname',
     'Sweden',
     'Switzerland',
     'Syria',
     'Taiwan',
     'Tajikistan',
     'Tanzania',
     'Thailand',
     'Timor-Leste',
     'Togo',
     'Tonga',
     'Trinidad and Tobago',
     'Tunisia',
     'Turkey',
     'Turkmenistan',
     'Tuvalu',
     'Uganda',
     'Ukraine',
     'United Arab Emirates',
     'United Kingdom',
     'United States',
     'Uruguay',
     'Uzbekistan',
     'Vanuatu',
     'Vatican City',
     'Venezuela',
     'Vietnam',
     'Yemen',
     'Zambia',
     'Zimbabwe',
];

const paymentOptions = [
     {
          value: 'local_debit_apple_pay',
          title: 'Secure Checkout',
          description: 'Pay securely with your debit or credit card, Apple Pay, or Google Pay. You’ll be redirected to a secure payment page to complete your purchase.',
          badge: 'Debit',
          logos: [
               { src: visaLogo, alt: 'Visa' },
               { src: masterCardLogo, alt: 'Mastercard' },
               { src: applePayLogo, alt: 'Apple Pay' },
               { src: napsLogo, alt: 'NAPS' },
               { src: amexLogo, alt: 'American Express' },
               { src: googlePayLogo, alt: 'Google Pay' },
          ],
     },
     // {
     //   value: 'credit_card_apple_pay',
     //   title: 'Credit Card / Apple Pay',
     //   description: 'Pay with a credit card or Apple Pay through secure checkout.',
     //   badge: 'Credit',
     //   logos: [
     //     { src: visaLogo, alt: 'Visa' },
     //     { src: masterCardLogo, alt: 'Mastercard' },
     //     { src: amexLogo, alt: 'American Express' },
     //   ],
     // },
];

const RegistrationDialog = ({
     open,
     onClose,
     registrationStep,
     onBackToDetails,
     selectedSession,
     selectedTicket,
     selectedTicketSeatsLeft,
     quantity,
     totalPrice,
     payableTotal,
     couponDiscount,
     isFreeTicket,
     couponCode,
     appliedCoupon,
     couponError,
     couponApplying,
     showCouponSection,
     onCouponCodeChange,
     onApplyCoupon,
     onRemoveCoupon,
     registerError,
     formErrors,
     registrationForm,
     onFormChange,
     onPaymentTypeChange,
     onMarketingConsentChange,
     additionalRegistrationFields,
     onAdditionalFieldChange,
     isSidraEmployeeOnly,
     sidraEmailDomain,
     sidraEmailPreview,
     sidraOtpStatus,
     sidraOtpSent,
     sidraOtpSending,
     sidraOtpVerifying,
     sidraOtpCooldownRemaining,
     onSendSidraOtp,
     onVerifySidraOtp,
     isStudentTicket,
     onStudentFileChange,
     registering,
     onSubmit,
}) => {
     const [attendeeExpanded, setAttendeeExpanded] = useState(false);

     useEffect(() => {
          if (open && registrationStep === 1) {
               setAttendeeExpanded(false);
          }
     }, [open, registrationStep]);

     const handleCouponKeyDown = (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          if (couponApplying || registering) return;
          if (appliedCoupon) onRemoveCoupon();
          else onApplyCoupon();
     };

     return (
          <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
               <DialogTitle sx={registrationDialogStyles.dialogTitle}>
                    {registrationStep === 1 && (
                         <IconButton edge='start' onClick={onBackToDetails} disabled={registering} aria-label='Back' sx={registrationDialogStyles.titleBackButton}>
                              <ArrowBackIcon />
                         </IconButton>
                    )}
                    <Box component='span' sx={registrationDialogStyles.titleText}>
                         {registrationStep === 0 ? 'Attendee Details' : isFreeTicket ? 'Review & Confirm' : 'Review & Payment'}
                    </Box>
                    <IconButton onClick={onClose} disabled={registering} aria-label='Close' sx={registrationDialogStyles.titleCloseButton}>
                         <CloseIcon />
                    </IconButton>
               </DialogTitle>
               <DialogContent>
                    <Stack spacing={2} sx={registrationDialogStyles.contentStack}>
                         <Stepper activeStep={registrationStep} alternativeLabel>
                              <Step>
                                   <StepLabel>Details</StepLabel>
                              </Step>
                              <Step>
                                   <StepLabel>{isFreeTicket ? 'Confirm' : 'Review'}</StepLabel>
                              </Step>
                         </Stepper>

                         {registerError && <Alert severity='error'>{registerError}</Alert>}
                         {formErrors.ticket && <Alert severity='error'>{formErrors.ticket}</Alert>}

                         {registrationStep === 0 ? (
                              <>
                                   <Grid container spacing={1.5}>
                                        <Grid size={{ xs: 12 }}>
                                             <TextField
                                                  required
                                                  label='Full Name'
                                                  value={registrationForm.fullName}
                                                  onChange={onFormChange('fullName')}
                                                  error={Boolean(formErrors.fullName)}
                                                  helperText={formErrors.fullName}
                                                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                                                  fullWidth
                                             />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                             {isSidraEmployeeOnly ? (
                                                  <Stack spacing={1}>
                                                       <Box sx={registrationDialogStyles.couponGroup}>
                                                            <TextField
                                                                 required
                                                                 type='text'
                                                                 label='Sidra Email'
                                                                 value={registrationForm.sidraEmailId}
                                                                 onChange={onFormChange('sidraEmailId')}
                                                                 error={Boolean(formErrors.sidraEmailId)}
                                                                 helperText={formErrors.sidraEmailId || `Provide your Sidra Email Address to receive a code.`}
                                                                 inputProps={{ maxLength: EMAIL_MAX_LENGTH, placeholder: 'abc or abc@sidra.org' }}
                                                                 sx={registrationDialogStyles.couponField}
                                                                 fullWidth
                                                            />
                                                            <Button
                                                                 variant='outlined'
                                                                 onClick={onSendSidraOtp}
                                                                 disabled={sidraOtpSending || sidraOtpVerifying || registering || sidraOtpCooldownRemaining > 0}
                                                                 sx={registrationDialogStyles.couponButton}
                                                            >
                                                                 {sidraOtpSending ? 'Sending...' : sidraOtpCooldownRemaining > 0 ? `Resend in (${sidraOtpCooldownRemaining}s)` : 'Send Code'}
                                                            </Button>
                                                       </Box>

                                                       {sidraOtpSent && (
                                                            <Box sx={registrationDialogStyles.couponGroup}>
                                                                 <TextField
                                                                      required
                                                                      type='text'
                                                                      label='Enter code received on your email'
                                                                      value={registrationForm.sidraOtp}
                                                                      onChange={onFormChange('sidraOtp')}
                                                                      error={Boolean(formErrors.sidraOtp)}
                                                                      helperText={formErrors.sidraOtp}
                                                                      inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                                                                      sx={registrationDialogStyles.couponField}
                                                                      fullWidth
                                                                 />
                                                                 <Button
                                                                      variant='contained'
                                                                      onClick={onVerifySidraOtp}
                                                                      disabled={sidraOtpVerifying || sidraOtpSending || registering}
                                                                      sx={registrationDialogStyles.couponButton}
                                                                 >
                                                                      {sidraOtpVerifying ? 'Verifying...' : 'Verify Code'}
                                                                 </Button>
                                                            </Box>
                                                       )}
                                                       {formErrors.sidraOtp && <Alert severity='error'>{formErrors.sidraOtp}</Alert>}
                                                       {sidraOtpStatus?.message && <Alert severity={sidraOtpStatus.severity || 'info'}>{sidraOtpStatus.message}</Alert>}
                                                  </Stack>
                                             ) : (
                                                  <TextField
                                                       required
                                                       type='email'
                                                       label='Email Address'
                                                       value={registrationForm.email}
                                                       onChange={onFormChange('email')}
                                                       error={Boolean(formErrors.email)}
                                                       helperText={formErrors.email}
                                                       inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
                                                       fullWidth
                                                  />
                                             )}
                                        </Grid>
                                   </Grid>

                                   <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                             <TextField
                                                  required
                                                  type='tel'
                                                  label='Phone Number'
                                                  value={registrationForm.phone}
                                                  onChange={onFormChange('phone')}
                                                  error={Boolean(formErrors.phone)}
                                                  helperText={formErrors.phone}
                                                  inputProps={{ maxLength: PHONE_MAX_LENGTH }}
                                                  fullWidth
                                             />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                             <Autocomplete
                                                  options={countries}
                                                  value={registrationForm.country || ''}
                                                  onChange={(_, newValue) => onFormChange('country')({ target: { value: newValue || '' } })}
                                                  renderInput={(params) => (
                                                       <TextField
                                                            {...params}
                                                            required
                                                            label='Country'
                                                            error={Boolean(formErrors.country)}
                                                            helperText={formErrors.country}
                                                            inputProps={{ ...params.inputProps, maxLength: COUNTRY_MAX_LENGTH }}
                                                       />
                                                  )}
                                                  fullWidth
                                             />
                                        </Grid>
                                   </Grid>

                                   {additionalRegistrationFields.length > 0 && (
                                        <Grid container spacing={2}>
                                             {additionalRegistrationFields.map((field) => {
                                                  const minLength = getFieldLengthValue(field.min_length);
                                                  const maxLength = getFieldLengthValue(field.max_length);
                                                  const fieldError = formErrors[`additionalFields.${field.field_name}`];
                                                  const isTextarea = field.field_type === 'textarea';

                                                  return (
                                                       <Grid size={{ xs: 12, sm: isTextarea ? 12 : 6 }} key={field.field_name}>
                                                            <TextField
                                                                 required={field.is_required}
                                                                 type={field.field_type === 'number' || field.field_type === 'email' ? field.field_type : 'text'}
                                                                 label={field.label}
                                                                 value={registrationForm.additionalFields[field.field_name] || ''}
                                                                 onChange={onAdditionalFieldChange(field.field_name)}
                                                                 error={Boolean(fieldError)}
                                                                 helperText={getAdditionalFieldHelperText(field, fieldError)}
                                                                 multiline={isTextarea}
                                                                 minRows={isTextarea ? 2 : undefined}
                                                                 inputProps={{
                                                                      inputMode: field.field_type === 'number' ? 'numeric' : 'text',
                                                                      pattern: field.field_type === 'number' ? '[0-9]*' : undefined,
                                                                      minLength: minLength === null ? undefined : minLength,
                                                                      maxLength: maxLength === null ? undefined : maxLength,
                                                                 }}
                                                                 fullWidth
                                                            />
                                                       </Grid>
                                                  );
                                             })}
                                        </Grid>
                                   )}

                                   {isStudentTicket && (
                                        <Box>
                                             <Button variant='outlined' component='label' fullWidth>
                                                  Upload Student ID Document
                                                  <input hidden type='file' accept='.pdf,.jpg,.jpeg,.png' onChange={onStudentFileChange} />
                                             </Button>
                                             {formErrors.studentFileName && (
                                                  <Typography variant='body2' color='error' sx={{ mt: 1 }}>
                                                       {formErrors.studentFileName}
                                                  </Typography>
                                             )}
                                             {registrationForm.studentFileName && (
                                                  <Typography variant='body2' color='success.main' sx={{ mt: 1 }}>{`Selected: ${registrationForm.studentFileName}`}</Typography>
                                             )}
                                        </Box>
                                   )}

                                   <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, fontSize: '0.875rem' }}>
                                             <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                                                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                       Occurrence / Session:
                                                  </Typography>
                                                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                                       {selectedSession?.title || '-'}
                                                  </Typography>
                                             </Box>
                                             <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                                                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                       Ticket:
                                                  </Typography>
                                                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                                       {selectedTicket ? `${selectedTicket.label}${quantity > 1 ? ` (x${quantity})` : ' (x1)'}` : '-'}
                                                  </Typography>
                                             </Box>
                                        </Box>
                                   </Box>
                              </>
                         ) : (
                              <Stack spacing={2}>
                                   <Accordion disableGutters expanded={attendeeExpanded} onChange={(_, expanded) => setAttendeeExpanded(expanded)} sx={registrationDialogStyles.attendeeAccordion}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={registrationDialogStyles.attendeeAccordionSummary}>
                                             <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                                                  Attendee details
                                             </Typography>
                                             <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 600 }} noWrap>
                                                  {registrationForm.fullName || 'Name'}
                                             </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={registrationDialogStyles.attendeeAccordionDetails}>
                                             <Grid container spacing={1}>
                                                  <Grid size={{ xs: 4 }}>
                                                       <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                            Name
                                                       </Typography>
                                                  </Grid>
                                                  <Grid size={{ xs: 8 }}>
                                                                    <Typography variant='body2'>{registrationForm.fullName || '-'}</Typography>
                                                  </Grid>

                                                  <Grid size={{ xs: 4 }}>
                                                       <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                            Email
                                                       </Typography>
                                                  </Grid>
                                                  <Grid size={{ xs: 8 }}>
                                                       <Typography variant='body2' color='text.secondary'>
                                                            {registrationForm.email || '-'}
                                                       </Typography>
                                                  </Grid>

                                                  <Grid size={{ xs: 4 }}>
                                                       <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                            Phone
                                                       </Typography>
                                                  </Grid>
                                                  <Grid size={{ xs: 8 }}>
                                                       <Typography variant='body2' color='text.secondary'>
                                                            {registrationForm.phone || '-'}
                                                       </Typography>
                                                  </Grid>

                                                  <Grid size={{ xs: 4 }}>
                                                       <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                            Country
                                                       </Typography>
                                                  </Grid>
                                                  <Grid size={{ xs: 8 }}>
                                                       <Typography variant='body2' color='text.secondary'>
                                                            {registrationForm.country || '-'}
                                                       </Typography>
                                                  </Grid>

                                                  {additionalRegistrationFields.map((field) => {
                                                       const value = registrationForm.additionalFields[field.field_name];
                                                       if (!value) return null;
                                                       return (
                                                            <Grid container size={{ xs: 12 }} key={field.field_name} spacing={1}>
                                                                 <Grid size={{ xs: 4 }}>
                                                                      <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                                           {field.label}
                                                                      </Typography>
                                                                 </Grid>
                                                                 <Grid size={{ xs: 8 }}>
                                                                      <Typography variant='body2' color='text.secondary'>
                                                                           {value}
                                                                      </Typography>
                                                                 </Grid>
                                                            </Grid>
                                                       );
                                                  })}

                                                  {registrationForm.studentFileName && (
                                                       <>
                                                            <Grid size={{ xs: 4 }}>
                                                                 <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
                                                                      Document
                                                                 </Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 8 }}>
                                                                 <Typography variant='body2' color='text.secondary'>
                                                                      {registrationForm.studentFileName}
                                                                 </Typography>
                                                            </Grid>
                                                       </>
                                                  )}
                                             </Grid>
                                        </AccordionDetails>
                                   </Accordion>

                                   {!isFreeTicket && (
                                        <Box>
                                             <Typography variant='subtitle2' sx={registrationDialogStyles.paymentTitle}>
                                                  Payment type
                                             </Typography>
                                             {formErrors.paymentType && (
                                                  <Typography variant='body2' color='error' sx={{ mb: 1 }}>
                                                       {formErrors.paymentType}
                                                  </Typography>
                                             )}
                                             <Grid container spacing={1.5}>
                                                  {paymentOptions.map((option) => {
                                                       const selected = registrationForm.paymentType === option.value;
                                                       return (
                                                            <Grid size={{ xs: 12 }} key={option.value}>
                                                                 <Box component='label' sx={registrationDialogStyles.paymentOption(selected)}>
                                                                      <input
                                                                           type='radio'
                                                                           name='paymentType'
                                                                           value={option.value}
                                                                           checked={selected}
                                                                           onChange={onPaymentTypeChange}
                                                                           style={registrationDialogStyles.paymentInput}
                                                                      />
                                                                      <Stack spacing={1} sx={{ height: '100%' }}>
                                                                           <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
                                                                                {selected && <CheckCircleIcon sx={registrationDialogStyles.paymentCheck} />}
                                                                           </Stack>
                                                                           <Typography sx={registrationDialogStyles.paymentOptionTitle}>{option.title}</Typography>
                                                                           <Typography variant='body2' color='text.secondary'>
                                                                                {option.description}
                                                                           </Typography>
                                                                           <Stack
                                                                                direction='row'
                                                                                spacing={0.75}
                                                                                justifyContent='flex-end'
                                                                                useFlexGap
                                                                                sx={{ ...registrationDialogStyles.paymentLogoGroup, flexWrap: 'wrap' }}
                                                                           >
                                                                                {option.logos.map((logo) => (
                                                                                     <Box key={logo.alt} sx={registrationDialogStyles.paymentLogoTile}>
                                                                                          <Box component='img' src={logo.src} alt={logo.alt} sx={registrationDialogStyles.paymentLogo} />
                                                                                     </Box>
                                                                                ))}
                                                                           </Stack>
                                                                      </Stack>
                                                                 </Box>
                                                            </Grid>
                                                       );
                                                  })}
                                             </Grid>
                                        </Box>
                                   )}

                                   {showCouponSection && (
                                        <Box>
                                             <Box sx={registrationDialogStyles.couponGroup}>
                                                  <TextField
                                                       label='Have a coupon?'
                                                       value={couponCode}
                                                       onChange={onCouponCodeChange}
                                                       onKeyDown={handleCouponKeyDown}
                                                       error={Boolean(couponError)}
                                                       helperText={couponError}
                                                       disabled={Boolean(appliedCoupon) || couponApplying || registering}
                                                       inputProps={{ maxLength: 50 }}
                                                       sx={registrationDialogStyles.couponField}
                                                       fullWidth
                                                  />
                                                  <Button
                                                       type='button'
                                                       variant={appliedCoupon ? 'outlined' : 'contained'}
                                                       onClick={appliedCoupon ? onRemoveCoupon : onApplyCoupon}
                                                       disabled={couponApplying || registering}
                                                       sx={registrationDialogStyles.couponButton}
                                                  >
                                                       {appliedCoupon ? 'Remove' : couponApplying ? 'Applying...' : 'Apply'}
                                                  </Button>
                                             </Box>
                                             {appliedCoupon && (
                                                  <Alert severity='success' sx={{ mt: 1 }}>
                                                       {appliedCoupon.is_complimentary
                                                            ? `${appliedCoupon.name || appliedCoupon.code} applied - your tickets are complimentary.`
                                                            : `${appliedCoupon.name || appliedCoupon.code} applied - you save ${formatCurrency(couponDiscount)}.`}
                                                  </Alert>
                                             )}
                                        </Box>
                                   )}

                                   <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'rgba(0,0,0,0.01)' }}>
                                        <input
                                             type='hidden'
                                             id='sidraConsentText'
                                             name='sidra_consent_text'
                                             value='I would like to receive marketing communications about future events. By registering for this event and/or if you choose to opt-in to receive marketing/event related communications for future events, you agree to our terms and acknowledge our Privacy Notice.'
                                        />

                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                             <input
                                                  type='radio'
                                                  name='sidra_marketing_consent'
                                                  id='sidraConsentYes'
                                                  value='1'
                                                  checked={Boolean(registrationForm.sidraMarketingConsent)}
                                                  onChange={() => onMarketingConsentChange(true)}
                                             />
                                             <label htmlFor='sidraConsentYes' style={{ cursor: 'pointer', color: '#1f2937', lineHeight: 1.5, fontSize: '0.95rem' }}>
                                                  I would like to receive marketing communications about future events.
                                                  <br />
                                                  <small style={{ color: '#4b5563' }}>
                                                       By registering for this event and/or if you choose to opt-in to receive marketing/event related communications for future events, you agree to
                                                       our
                                                       <a href='https://payments-test.sidra.org/terms_and_conditions' target='_blank' rel='noopener noreferrer' style={{ color: '#1976d2' }}>
                                                            terms
                                                       </a>
                                                       and acknowledge our
                                                       <a href='https://www.sidra.org/privacy-policy/' target='_blank' rel='noopener noreferrer' style={{ color: '#1976d2' }}>
                                                            Privacy Notice
                                                       </a>
                                                       .
                                                  </small>
                                             </label>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                                             <input
                                                  type='radio'
                                                  name='sidra_marketing_consent'
                                                  id='sidraConsentNo'
                                                  value='0'
                                                  checked={!Boolean(registrationForm.sidraMarketingConsent)}
                                                  onChange={() => onMarketingConsentChange(false)}
                                             />
                                             <label htmlFor='sidraConsentNo' style={{ cursor: 'pointer', color: '#1f2937', fontWeight: 600 }}>
                                                  I don’t agree.
                                             </label>
                                        </Box>
                                   </Box>
                              </Stack>
                         )}
                    </Stack>
               </DialogContent>
               <DialogActions sx={registrationStep === 1 ? registrationDialogStyles.actionsWithTotal : registrationDialogStyles.actions}>
                    {registrationStep === 1 && (
                         <Box sx={registrationDialogStyles.amountSummary}>
                              {couponDiscount > 0 && (
                                   <Box sx={{ ...registrationDialogStyles.amountRow, mb: 0.25 }}>
                                        <Typography variant='body1' color='success.main' sx={registrationDialogStyles.amountDiscount}>
                                             Discount
                                        </Typography>
                                        <Typography variant='body1' color='success.main' sx={registrationDialogStyles.amountDiscount}>
                                             -{formatCurrency(couponDiscount)}
                                        </Typography>
                                   </Box>
                              )}
                              <Box sx={registrationDialogStyles.amountRow}>
                                   <Typography sx={registrationDialogStyles.amountTotalLabel}>Total</Typography>
                                   <Typography sx={registrationDialogStyles.amountTotalValue}>{formatCurrency(payableTotal ?? totalPrice)}</Typography>
                              </Box>
                         </Box>
                    )}
                    <Box sx={registrationDialogStyles.submitGroup}>
                         <Button
                              variant='contained'
                              disabled={registering || (!isFreeTicket && registrationStep === 1 && !registrationForm.paymentType)}
                              onClick={onSubmit}
                              sx={registrationDialogStyles.primaryButton}
                         >
                              {registrationStep === 0
                                   ? 'Review Details'
                                   : registering
                                     ? isFreeTicket
                                          ? 'Registering...'
                                          : 'Processing...'
                                     : isFreeTicket
                                       ? 'Complete Registration'
                                       : 'Proceed to Payment'}
                         </Button>
                         <Typography sx={registrationDialogStyles.termsNotice}>
                              By continuing, you agree to the{' '}
                              <Link href='https://payments.sidra.org/terms_and_conditions' target='_blank' rel='noopener noreferrer' underline='none' sx={registrationDialogStyles.termsLink}>
                                   Terms and Conditions
                              </Link>
                              .
                         </Typography>
                    </Box>
               </DialogActions>
          </Dialog>
     );
};

export default RegistrationDialog;
